import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField, Tooltip, IconButton } from '@mui/material';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

const Overlay = styled('div')({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.18)',
  zIndex: 1299,
});

const StyledContainer = styled('div')({
  position: 'fixed',
  top: '16%',
  left: '35%',
  transform: 'translateX(-50%)',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '16px',
  maxHeight: '75vh',
  overflowY: 'auto',
  minWidth: '500px',
  maxWidth: '560px',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
  border: '1px solid #304fff',
});

function ProjectModal({
  open,
  mode,
  direction,
  formData,
  existingProjects,
  onClose,
  onSave,
  loading,
  zIndex = 1300,
}) {
  const { t } = useTranslation();
  const nodeRef = useRef(null);
  const getNextProjectCode = (projectSources = []) => {
    const maxCode = Math.max(
      0,
      ...projectSources.map((project) => parseInt(project?.code, 10) || 0)
    );

    return `${maxCode + 1}`;
  };

  const [projectRows, setProjectRows] = useState([{ code: getNextProjectCode(existingProjects), name: '' }]);

  const inputPropsStyle = useMemo(
    () => ({
      disableUnderline: true,
      className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
      sx: {
        height: '48px',
        padding: '0 10px',
        borderRadius: '8px',
        background: '#F5F5F6',
        border: '1px solid transparent',
        '&:focus-within': {
          border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
        },
      },
    }),
    [direction]
  );

  const inputStyle = useMemo(
    () => ({
      style: {
        textAlign: direction === 'rtl' ? 'right' : 'left',
        direction: direction === 'rtl' ? 'rtl' : 'ltr',
      },
    }),
    [direction]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'create') {
      setProjectRows([{ code: getNextProjectCode(existingProjects), name: '' }]);
      return;
    }

    setProjectRows([
      {
        code: formData.code ?? '',
        name: formData.name ?? '',
      },
    ]);
  }, [existingProjects, formData.code, formData.name, mode, open]);

  const isFormValid = projectRows.every(
    (project) => project.code.trim() !== '' && project.name.trim() !== ''
  );
  const title = mode === 'create' ? t('AddProject') : `${t('Update')} ${t('Projects')}`;

  const handleChange = (index, field, value) => {
    setProjectRows((current) =>
      current.map((project, projectIndex) =>
        projectIndex === index ? { ...project, [field]: value } : project
      )
    );
  };

  const handleAddRow = () => {
    setProjectRows((current) => [
      ...current,
      { code: getNextProjectCode([...existingProjects, ...current]), name: '' },
    ]);
  };

  const handleRemoveRow = (index) => {
    setProjectRows((current) => current.filter((_, projectIndex) => projectIndex !== index));
  };

  const handleSave = async () => {
    if (!isFormValid || loading) {
      return;
    }

    if (mode === 'create') {
      const success = await onSave(projectRows);
      if (success) {
        setProjectRows([
          {
            code: getNextProjectCode([...existingProjects, ...projectRows]),
            name: '',
          },
        ]);
      }
      return;
    }

    await onSave({
      code: projectRows[0]?.code ?? '',
      name: projectRows[0]?.name ?? '',
    });
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Overlay onClick={onClose} sx={{ zIndex: zIndex - 1 }} />
      <Draggable handle=".handle" nodeRef={nodeRef}>
        <StyledContainer ref={nodeRef} style={{ direction, zIndex }}>
          <div
            className="handle"
            style={{
              cursor: 'move',
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Tooltip title={t('Exit')}>
              <IconButton onClick={onClose}>
                <CancelIcon style={{ color: '#304FFF' }} />
              </IconButton>
            </Tooltip>
            <div className="edit-title">{title}</div>
            <div />
          </div>

          {projectRows.map((project, index) => (
            <div key={`${mode}-project-row-${index}`} >
              {index > 0 && (
                <hr
                  style={{
                    border: '0.5px solid #ddd',
                    marginBottom: 16,
                  }}
                />
              )}

              <div
                className="form-row"
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                  gap: 8,
                  width: '100%',
                }}
              >
                <div
                  className="edit-form-row-item"
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    flexDirection: 'column',
                    display: 'flex',
                    padding: '0 6px',
                  }}
                >
                  <div className="edit-form-row-item-title">
                    {t('ProjectCode')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('ProjectCode')}
                    value={project.code}
                    onChange={(event) => handleChange(index, 'code', event.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    error={project.code.trim() === ''}
                    helperText={project.code.trim() === '' ? t('Required') : ''}
                  />
                </div>

                <div
                  className="edit-form-row-item"
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    flexDirection: 'column',
                    display: 'flex',
                    padding: '0 6px',
                  }}
                >
                  <div className="edit-form-row-item-title">
                    {t('ProjectName')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('ProjectName')}
                    value={project.name}
                    onChange={(event) => handleChange(index, 'name', event.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    error={project.name.trim() === ''}
                    helperText={project.name.trim() === '' ? t('Required') : ''}
                  />
                </div>

                {mode === 'create' && projectRows.length > 1 && (
                  <Tooltip title={t('Delete')}>
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 3 }}
                    >
                      <DeleteIcon style={{ color: 'red' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          ))}

          {mode === 'create' && (
            <div
              onClick={handleAddRow}
              className="edit-add-button"
              style={{
                alignSelf: direction === 'rtl' ? 'flex-start' : 'flex-end',
                marginBottom: 16,
                cursor: 'pointer',
                padding: '6px 16px',
                backgroundColor: '#304FFF',
                color: '#fff',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                width: 'fit-content',
              }}
            >
              {t('AddProject')}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px', gap: '10px' }}>
            <div onClick={onClose} className="edit-cancel-button">
              {t('Cancel')}
            </div>
            <div
              onClick={!isFormValid || loading ? undefined : handleSave}
              className={`edit-save-button ${!isFormValid || loading ? 'disabled' : ''}`}
              style={{
                opacity: !isFormValid || loading ? 0.5 : 1,
                cursor: !isFormValid || loading ? 'not-allowed' : 'pointer',
                backgroundColor: !isFormValid || loading ? '#ccc' : undefined,
              }}
            >
              {mode === 'create' ? t('Add') : t('Update')}
            </div>
          </div>
        </StyledContainer>
      </Draggable>
    </>
  );
}

ProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'update']).isRequired,
  direction: PropTypes.string,
  formData: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  existingProjects: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  zIndex: PropTypes.number,
};

ProjectModal.defaultProps = {
  direction: 'rtl',
  existingProjects: [],
  loading: false,
  zIndex: 1300,
};

export default ProjectModal;
